import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../api/adminApi";
import type { ReportDefinition, ReportFolder, ReportStatus } from "../../api/types";
import { DataTable } from "../../components/DataTable";
import { normalizeReportKey } from "../../utils/reportKey";

const STATUS_BADGE: Record<ReportStatus, string> = {
  DRAFT: "badge badge-draft",
  PUBLISHED: "badge badge-published",
  ARCHIVED: "badge badge-archived",
};

export default function ReportList() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportDefinition[]>([]);
  const [folders, setFolders] = useState<ReportFolder[]>([]);
  const [folderFilter, setFolderFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newName, setNewName] = useState("");
  // Until the key is edited by hand it follows the display name ("Cashbook Payment" ->
  // CASHBOOK_PAYMENT), so most people never have to think about it.
  const [keyTouched, setKeyTouched] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function load() {
    setLoading(true);
    Promise.all([adminApi.reports.list(), adminApi.folders.list()])
      .then(([r, f]) => {
        setReports(r);
        setFolders(f);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load reports"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  const folderName = (id: string | null) => folders.find((f) => f.id === id)?.name ?? "(none)";

  const filtered = useMemo(
    () =>
      reports.filter(
        (r) =>
          (!folderFilter || r.folderId === folderFilter) &&
          (!statusFilter || r.status === statusFilter)
      ),
    [reports, folderFilter, statusFilter]
  );

  const finalKey = normalizeReportKey(newKey);
  const keyTaken = !!finalKey && reports.some((r) => r.reportKey.toUpperCase() === finalKey);

  async function handleCreate() {
    if (!finalKey || !newName.trim() || keyTaken) return;
    setSubmitting(true);
    setCreateError(null);
    try {
      const created = await adminApi.reports.create({
        reportKey: finalKey,
        displayName: newName.trim(),
        status: "DRAFT",
        defaultFormat: "pdf",
        defaultPageLayout: "LANDSCAPE",
      });
      setCreating(false);
      setNewKey("");
      setNewName("");
      setKeyTouched(false);
      navigate(`/reports/${created.id}/designer`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create report");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1>Reports</h1>
        <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
          New report
        </button>
      </div>

      {error && <div className="error-text">{error}</div>}

      {creating && (
        <div className="page-panel">
          <h3>New report</h3>
          {createError && <div className="error-text">{createError}</div>}
          <div className="form-grid">
            <div className="form-row">
              <label>Display name</label>
              <input
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  if (!keyTouched) setNewKey(normalizeReportKey(e.target.value, true));
                }}
                placeholder="Cashbook Payment"
              />
            </div>
            <div className="form-row">
              <label>Report key (unique, used in the API)</label>
              <input
                value={newKey}
                onChange={(e) => {
                  setKeyTouched(true);
                  setNewKey(normalizeReportKey(e.target.value, true));
                }}
                placeholder="CASHBOOK_PAYMENT"
                style={{ fontFamily: "var(--font-mono)" }}
              />
              {keyTaken ? (
                <span className="error-text">A report with key {finalKey} already exists — pick a different one.</span>
              ) : (
                <span className="help-text">
                  Auto-formatted to UPPER_SNAKE_CASE. This is what your integration sends to request the report, and it can't be changed later.
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="btn-primary"
              onClick={handleCreate}
              disabled={submitting || keyTaken || !finalKey || !newName.trim()}
            >
              {submitting ? "Creating..." : "Create"}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setCreating(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="page-panel">
        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
          <div className="form-row" style={{ marginBottom: 0 }}>
            <label>Folder</label>
            <select value={folderFilter} onChange={(e) => setFolderFilter(e.target.value)}>
              <option value="">All folders</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row" style={{ marginBottom: 0 }}>
            <label>Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="PUBLISHED">Published</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <DataTable
            rows={filtered}
            rowKey={(r) => r.id}
            onRowClick={(r) => navigate(`/reports/${r.id}/settings`)}
            emptyMessage="No reports match these filters."
            columns={[
              { key: "displayName", header: "Name" },
              { key: "reportKey", header: "Key" },
              { key: "category", header: "Category" },
              { key: "folderId", header: "Folder", render: (r) => folderName(r.folderId) },
              {
                key: "status",
                header: "Status",
                render: (r) => <span className={STATUS_BADGE[r.status]}>{r.status}</span>,
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}
